import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Divider,
  Alert,
  InputAdornment,
  IconButton,
  Pagination,
  Skeleton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Stack,
  Avatar,
  Fade,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  Search as SearchIcon,
  MenuBook as MenuBookIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Article as ArticleIcon,
  Science as ScienceIcon,
  LibraryBooks as LibraryIcon,
  OpenInNew as OpenInNewIcon,
  Inventory2 as InventoryIcon,
  Category as CategoryIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { buscarArquivosDigitais } from '../services/arquivoDigitalService';
import { getDocumentUrl } from '../utils/apiUtils';
import FormularioArquivamentoPublico from '../components/Repositorio/FormularioArquivamentoPublico';
import api from '../services/api';
import useAuth from '../hooks/useAuth';

const TIPO_CORES = {
  Tese: { bg: '#7c3aed', light: '#ede9fe', label: 'Tese' },
  Dissertação: { bg: '#0369a1', light: '#e0f2fe', label: 'Dissertação' },
  Monografia: { bg: '#047857', light: '#d1fae5', label: 'Monografia' },
};

const ITEMS_POR_PAGINA = 9;

function CardSkeleton() {
  return (
    <Card sx={{ height: 260, borderRadius: 3 }}>
      <CardContent>
        <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1, mb: 2 }} />
        <Skeleton variant="text" sx={{ fontSize: '1.1rem', mb: 0.5 }} />
        <Skeleton variant="text" width="60%" sx={{ mb: 2 }} />
        <Skeleton variant="text" width="45%" sx={{ mb: 1 }} />
        <Skeleton variant="text" width="55%" sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={70} height={24} />
          <Skeleton variant="rounded" width={90} height={24} />
          <Skeleton variant="rounded" width={60} height={24} />
        </Box>
      </CardContent>
    </Card>
  );
}

function StatCard({ icon, value, label, color }) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        px: 3,
        py: 1.5,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.12)',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255,255,255,0.2)',
        minWidth: 110,
      }}
    >
      <Box sx={{ color: 'white', mb: 0.5 }}>{icon}</Box>
      <Typography variant="h5" fontWeight={700} color="white">
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
        {label}
      </Typography>
    </Box>
  );
}

StatCard.propTypes = {
  icon: PropTypes.node.isRequired,
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  color: PropTypes.string,
};

function DocumentoCard({ arquivo }) {
  const metadados = arquivo.metadados || arquivo.metadados_monografia || arquivo.MetadadosMonografia;
  const isPublico = !arquivo.monografia_id && !!arquivo.titulo_publico;

  const titulo =
    arquivo.titulo_publico ||
    arquivo.monografia?.projeto?.proposta?.titulo ||
    `Documento #${arquivo.id}`;
  const autor =
    metadados?.nome_autor ||
    arquivo.monografia?.projeto?.proposta?.estudante?.nome ||
    null;
  const tipoMaterial = metadados?.tipo_material || null;
  const tipoTitulacao = metadados?.tipo_titulacao || null;
  const area = metadados?.area_conhecimento || null;
  const supervisor = metadados?.supervisor || null;
  const dataDefesa = metadados?.data_defesa || arquivo.data_indexacao;
  const palavras = Array.isArray(arquivo.palavras_chave) ? arquivo.palavras_chave : [];

  const cor = TIPO_CORES[tipoMaterial] || TIPO_CORES['Monografia'];

  // Documento pode vir de monografia interna ou de depósito público
  const ficheiroNome = arquivo.documento_publico || arquivo.monografia?.documento_monografia;
  const temDocumento = !!ficheiroNome;

  return (
    <Fade in timeout={400}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          transition: 'all 0.25s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
            borderColor: 'primary.light',
          },
        }}
      >
        {/* Topo colorido */}
        <Box
          sx={{
            height: 6,
            bgcolor: cor.bg,
            borderRadius: '12px 12px 0 0',
          }}
        />

        <CardContent sx={{ flex: 1, p: 2.5 }}>
          {/* Badge tipo */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
            {tipoMaterial && (
              <Chip
                label={tipoMaterial}
                size="small"
                sx={{
                  bgcolor: cor.light,
                  color: cor.bg,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 22,
                }}
              />
            )}
            {tipoTitulacao && (
              <Chip
                label={tipoTitulacao}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            )}
          </Box>

          {/* Título */}
          <Tooltip title={titulo} placement="top">
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                mb: 1.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.4,
                cursor: 'default',
              }}
            >
              {titulo}
            </Typography>
          </Tooltip>

          {/* Autor */}
          {autor && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <Avatar sx={{ width: 20, height: 20, bgcolor: 'primary.light', fontSize: '0.65rem' }}>
                {autor.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="body2" color="text.secondary" noWrap>
                {autor}
              </Typography>
            </Box>
          )}

          {/* Supervisor */}
          {supervisor && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
              <SchoolIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {supervisor}
              </Typography>
            </Box>
          )}

          {/* Área */}
          {area && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
              <CategoryIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {area}
              </Typography>
            </Box>
          )}

          {/* Data */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
            <CalendarIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary">
              {dataDefesa
                ? new Date(dataDefesa).toLocaleDateString('pt-PT', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })
                : '—'}
            </Typography>
          </Box>

          {/* Palavras-chave */}
          {palavras.length > 0 && (
            <>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {palavras.slice(0, 4).map((p, i) => (
                  <Chip
                    key={i}
                    label={p}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: 'action.hover',
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                ))}
                {palavras.length > 4 && (
                  <Chip
                    label={`+${palavras.length - 4}`}
                    size="small"
                    sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 1 } }}
                  />
                )}
              </Box>
            </>
          )}
        </CardContent>

        <CardActions sx={{ px: 2.5, pb: 2, pt: 0, flexDirection: 'column', gap: 0.5 }}>
          {isPublico && (
            <Chip
              label="Depósito Público"
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20, alignSelf: 'flex-start', color: 'text.secondary', borderColor: 'divider' }}
            />
          )}
          <Button
            variant={temDocumento ? 'contained' : 'outlined'}
            size="small"
            fullWidth
            startIcon={<OpenInNewIcon fontSize="small" />}
            href={temDocumento ? getDocumentUrl(ficheiroNome, true) : '#'}
            target="_blank"
            disabled={!temDocumento}
            sx={{ borderRadius: 2, fontSize: '0.75rem' }}
          >
            {temDocumento ? 'Ver Documento' : 'Sem ficheiro'}
          </Button>
        </CardActions>
      </Card>
    </Fade>
  );
}

DocumentoCard.propTypes = {
  arquivo: PropTypes.shape({
    id: PropTypes.number,
    monografia_id: PropTypes.number,
    titulo_publico: PropTypes.string,
    documento_publico: PropTypes.string,
    data_indexacao: PropTypes.string,
    palavras_chave: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
    metadados: PropTypes.object,
    metadados_monografia: PropTypes.object,
    MetadadosMonografia: PropTypes.object,
    monografia: PropTypes.shape({
      documento_monografia: PropTypes.string,
      projeto: PropTypes.shape({
        proposta: PropTypes.shape({
          titulo: PropTypes.string,
          estudante: PropTypes.shape({
            nome: PropTypes.string,
          }),
        }),
      }),
    }),
  }).isRequired,
};

const DEFAULT_CONFIG = {
  institucional: false,
  titulo: 'Repositório Digital Académico',
  slogan: 'Consulte monografias, dissertações e teses arquivadas pela instituição',
  logo: null,
  hero_imagem: null,
  cor_primaria: '#1565C0',
  cor_secundaria: '#0D47A1',
};

const RepositorioPage = () => {
  const { user } = useAuth();
  const [termo, setTermo] = useState('');
  const [inputTermo, setInputTermo] = useState('');
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroArea, setFiltroArea] = useState('');
  const [dialogAberto, setDialogAberto] = useState(false);
  const [configRepo, setConfigRepo] = useState(DEFAULT_CONFIG);

  useEffect(() => {
    const host = window.location.hostname;
    api.get(`/instituicoes/publico/repositorio-config?host=${encodeURIComponent(host)}`)
      .then(({ data }) => setConfigRepo({ ...DEFAULT_CONFIG, ...data }))
      .catch(() => {});
    carregarDados('');
  }, []);

  const carregarDados = async (termoBusca) => {
    try {
      setLoading(true);
      setError(null);
      const data = await buscarArquivosDigitais(termoBusca || undefined, user?.instituicaoId);
      setResultados(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Não foi possível carregar o repositório. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setTermo(inputTermo);
    setPage(1);
    setFiltroTipo('');
    setFiltroArea('');
    carregarDados(inputTermo);
  };

  const handleClear = () => {
    setInputTermo('');
    setTermo('');
    setFiltroTipo('');
    setFiltroArea('');
    setPage(1);
    carregarDados('');
  };

  // Derivar áreas únicas para o filtro
  const areasDisponiveis = useMemo(() => {
    const areas = resultados
      .map((a) => a.metadados_monografia?.area_conhecimento || a.MetadadosMonografia?.area_conhecimento)
      .filter(Boolean);
    return [...new Set(areas)].sort();
  }, [resultados]);

  // Aplicar filtros locais (tipo/área)
  const resultadosFiltrados = useMemo(() => {
    return resultados.filter((a) => {
      const meta = a.metadados_monografia || a.MetadadosMonografia;
      if (filtroTipo && meta?.tipo_material !== filtroTipo) return false;
      if (filtroArea && meta?.area_conhecimento !== filtroArea) return false;
      return true;
    });
  }, [resultados, filtroTipo, filtroArea]);

  // Estatísticas
  const stats = useMemo(() => {
    const tipos = resultados.reduce((acc, a) => {
      const t = (a.metadados_monografia || a.MetadadosMonografia)?.tipo_material;
      if (t) acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {});
    return {
      total: resultados.length,
      areas: areasDisponiveis.length,
      teses: tipos['Tese'] || 0,
      dissertacoes: tipos['Dissertação'] || 0,
      monografias: tipos['Monografia'] || 0,
    };
  }, [resultados, areasDisponiveis]);

  const totalPages = Math.ceil(resultadosFiltrados.length / ITEMS_POR_PAGINA);
  const paginados = resultadosFiltrados.slice(
    (page - 1) * ITEMS_POR_PAGINA,
    page * ITEMS_POR_PAGINA
  );

  const temFiltros = filtroTipo || filtroArea;

  const tema = useMemo(() =>
    createTheme({
      palette: {
        primary: {
          main: configRepo.cor_primaria || '#1565C0',
          dark: configRepo.cor_secundaria || '#0D47A1',
        },
      },
    }),
    [configRepo.cor_primaria, configRepo.cor_secundaria]
  );

  const heroBg = `linear-gradient(135deg, ${configRepo.cor_secundaria || '#0D47A1'} 0%, ${configRepo.cor_primaria || '#1565C0'} 60%, ${configRepo.cor_primaria || '#1565C0'}cc 100%)`;

  return (
    <ThemeProvider theme={tema}>
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Hero */}
      <Box
        sx={{
          background: heroBg,
          pt: { xs: 5, md: 7 },
          pb: { xs: 6, md: 8 },
          px: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -40,
            left: 0,
            right: 0,
            height: 80,
            bgcolor: 'background.default',
            borderRadius: '50% 50% 0 0 / 40px',
          },
        }}
      >
        <Container maxWidth="lg">
          {/* Título */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.15)',
                mb: 2,
                overflow: 'hidden',
              }}
            >
              {configRepo.logo ? (
                <Box
                  component="img"
                  src={configRepo.logo}
                  alt="logo"
                  sx={{ width: 56, height: 56, objectFit: 'contain' }}
                />
              ) : (
                <LibraryIcon sx={{ fontSize: 34, color: 'white' }} />
              )}
            </Box>
            <Typography variant="h3" fontWeight={700} color="white" gutterBottom>
              {configRepo.titulo}
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 400, maxWidth: 560, mx: 'auto' }}
            >
              {configRepo.slogan}
            </Typography>
          </Box>

          {/* Barra de busca */}
          <Box sx={{ maxWidth: 680, mx: 'auto', mb: 4 }}>
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              }}
            >
              <TextField
                fullWidth
                placeholder="Pesquisar por título, autor, palavras-chave..."
                variant="outlined"
                value={inputTermo}
                onChange={(e) => setInputTermo(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: inputTermo ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClear}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  sx: { borderRadius: 0, '& fieldset': { border: 'none' } },
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                sx={{
                  height: 56,
                  px: 3,
                  borderRadius: 0,
                  boxShadow: 'none',
                  bgcolor: 'primary.dark',
                  '&:hover': { bgcolor: '#0d47a1' },
                  whiteSpace: 'nowrap',
                }}
              >
                Pesquisar
              </Button>
            </Paper>
          </Box>

          {/* Botão depositar */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<CloudUploadIcon />}
              onClick={() => setDialogAberto(true)}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.6)',
                borderRadius: 3,
                px: 3,
                py: 1,
                backdropFilter: 'blur(8px)',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.2)',
                },
              }}
            >
              Depositar Documento
            </Button>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'rgba(255,255,255,0.65)' }}>
              Investigadores e autores podem submeter os seus trabalhos gratuitamente
            </Typography>
          </Box>

          {/* Estatísticas */}
          {!loading && stats.total > 0 && (
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ gap: 1.5 }}
            >
              <StatCard
                icon={<InventoryIcon />}
                value={stats.total}
                label="Documentos"
              />
              <StatCard
                icon={<CategoryIcon />}
                value={stats.areas}
                label="Áreas"
              />
              {stats.teses > 0 && (
                <StatCard icon={<ScienceIcon />} value={stats.teses} label="Teses" />
              )}
              {stats.dissertacoes > 0 && (
                <StatCard icon={<ArticleIcon />} value={stats.dissertacoes} label="Dissertações" />
              )}
              {stats.monografias > 0 && (
                <StatCard icon={<MenuBookIcon />} value={stats.monografias} label="Monografias" />
              )}
            </Stack>
          )}
        </Container>
      </Box>

      {/* Conteúdo principal */}
      <Container maxWidth="lg" sx={{ pt: 5, pb: 6 }}>

        {/* Filtros e contagem */}
        {!loading && resultados.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
              <FilterIcon fontSize="small" />
              <Typography variant="body2">Filtrar:</Typography>
            </Box>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Tipo de documento</InputLabel>
              <Select
                value={filtroTipo}
                label="Tipo de documento"
                onChange={(e) => { setFiltroTipo(e.target.value); setPage(1); }}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="Tese">Tese</MenuItem>
                <MenuItem value="Dissertação">Dissertação</MenuItem>
                <MenuItem value="Monografia">Monografia</MenuItem>
              </Select>
            </FormControl>

            {areasDisponiveis.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Área de conhecimento</InputLabel>
                <Select
                  value={filtroArea}
                  label="Área de conhecimento"
                  onChange={(e) => { setFiltroArea(e.target.value); setPage(1); }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {areasDisponiveis.map((a) => (
                    <MenuItem key={a} value={a}>{a}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {temFiltros && (
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={() => { setFiltroTipo(''); setFiltroArea(''); setPage(1); }}
                sx={{ color: 'text.secondary' }}
              >
                Limpar filtros
              </Button>
            )}

            <Box sx={{ ml: 'auto' }}>
              <Typography variant="body2" color="text.secondary">
                {resultadosFiltrados.length === resultados.length
                  ? `${resultados.length} documento${resultados.length !== 1 ? 's' : ''}`
                  : `${resultadosFiltrados.length} de ${resultados.length} documentos`}
                {termo && ` para "${termo}"`}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Erro */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Skeleton loading */}
        {loading && (
          <Grid container spacing={3}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <CardSkeleton />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Resultados */}
        {!loading && paginados.length > 0 && (
          <>
            <Grid container spacing={3}>
              {paginados.map((arquivo) => (
                <Grid item xs={12} sm={6} md={4} key={arquivo.id}>
                  <DocumentoCard arquivo={arquivo} />
                </Grid>
              ))}
            </Grid>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => { setPage(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  color="primary"
                  size="large"
                  shape="rounded"
                />
              </Box>
            )}
          </>
        )}

        {/* Estado vazio */}
        {!loading && !error && resultadosFiltrados.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 10,
              px: 3,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'action.hover',
                mb: 3,
              }}
            >
              <LibraryIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {termo || temFiltros
                ? 'Nenhum documento encontrado'
                : 'O repositório ainda não tem documentos'}
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
              {termo
                ? `Não foram encontrados resultados para "${termo}".`
                : temFiltros
                ? 'Tenta remover os filtros para ver mais resultados.'
                : 'Os documentos arquivados aparecerão aqui.'}
            </Typography>
            {(termo || temFiltros) && (
              <Button variant="outlined" onClick={handleClear} sx={{ borderRadius: 2 }}>
                Limpar pesquisa
              </Button>
            )}
          </Box>
        )}
      </Container>
      {/* Dialog — Depositar Documento */}
      <Dialog
        open={dialogAberto}
        onClose={() => setDialogAberto(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ bgcolor: 'primary.main', borderRadius: 2, p: 0.75, display: 'flex' }}>
              <CloudUploadIcon sx={{ color: 'white', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>Depositar Documento</Typography>
              <Typography variant="caption" color="text.secondary">
                Submeta o seu trabalho académico no repositório digital
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setDialogAberto(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          <FormularioArquivamentoPublico
            onSuccess={() => {
              setDialogAberto(false);
              carregarDados('');
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
    </ThemeProvider>
  );
};

export default RepositorioPage;
